/**
 * Guest alert opt-ins. Source of truth = localStorage; a snapshot (ZIP, radius,
 * list items, weekly budget, timezone, opt-ins) + browser push subscription is
 * synced to the backend so the alert jobs can reach the guest.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ID_KEY = "guest_id";
const PREFS_KEY = "guest_alert_prefs";
export const GUEST_BUDGET_KEY = "guest_monthly_budget";

export const GUEST_ALERT_TYPES = [
  { key: "deal_alerts", title: "Grocery List Saver", desc: "When an item on your list goes on sale at a store near you" },
  { key: "community_deals", title: "Community Deals", desc: "When shoppers post a new deal within your radius" },
  { key: "budget_deal_alerts", title: "Budget Friendly Deals", desc: "Cheap picks near you that match your list, every morning" },
  { key: "budget_reminder", title: "Weekly Budget Reminder", desc: "Your weekly budget plus deals that fit it" },
];

const DEFAULT_PREFS = {
  deal_alerts: false, community_deals: false, budget_deal_alerts: false, budget_reminder: false,
  budget_reminder_day: "sun",
};

export function getGuestId() {
  let id = localStorage.getItem(ID_KEY);
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16));
    localStorage.setItem(ID_KEY, id);
  }
  return id;
}

export function getGuestAlertPrefs() {
  try {
    return { ...DEFAULT_PREFS, ...(JSON.parse(localStorage.getItem(PREFS_KEY)) || {}) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function saveGuestAlertPrefs(prefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

export function guestHasAnyAlert(prefs = getGuestAlertPrefs()) {
  return GUEST_ALERT_TYPES.some((t) => prefs[t.key]);
}

function snapshot() {
  const prefs = getGuestAlertPrefs();
  let items = [];
  try {
    const lists = JSON.parse(localStorage.getItem("guest_grocery_lists")) || [];
    items = lists.flatMap((l) => (l.items || []).filter((i) => !i.completed).map((i) => i.name)).filter(Boolean);
  } catch { /* ignore */ }
  const monthly = parseFloat(localStorage.getItem(GUEST_BUDGET_KEY) || "0") || 0;
  return {
    guest_id: getGuestId(),
    prefs: Object.fromEntries(GUEST_ALERT_TYPES.map((t) => [t.key, !!prefs[t.key]])),
    budget_reminder_day: prefs.budget_reminder_day || "sun",
    zip: localStorage.getItem("guest_zip") || "",
    radius: parseFloat(localStorage.getItem("guest_radius") || "10") || 10,
    items: [...new Set(items)],
    weekly_budget: Math.round((monthly / 4.33) * 100) / 100,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  };
}

export async function syncGuestAlertProfile(subscription = null) {
  const body = snapshot();
  if (subscription) body.subscription = subscription;
  const res = await fetch(`${BACKEND_URL}/api/push/guest/profile`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.ok ? res.json() : null;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const raw = window.atob((base64String + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/** Ask for browser permission, subscribe, and sync. Returns {ok, reason}. */
export async function enableGuestPush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return { ok: false, reason: "unsupported" };
  if (Notification.permission === "denied") return { ok: false, reason: "denied" };
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: "denied" };
  const reg = await navigator.serviceWorker.register("/sw-push.js");
  await navigator.serviceWorker.ready;
  const { public_key } = await (await fetch(`${BACKEND_URL}/api/push/vapid-key`)).json();
  if (!public_key) return { ok: false, reason: "server" };
  const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({
    userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(public_key),
  }));
  const data = await syncGuestAlertProfile(sub.toJSON());
  return { ok: !!data?.push_subscribed, reason: data ? "" : "server" };
}

export async function getGuestPushSubscription() {
  try {
    const reg = await navigator.serviceWorker?.getRegistration("/sw-push.js");
    return reg ? await reg.pushManager.getSubscription() : null;
  } catch {
    return null;
  }
}

export async function fetchGuestNotifications() {
  const res = await fetch(`${BACKEND_URL}/api/push/guest/notifications?guest_id=${getGuestId()}`);
  return res.ok ? (await res.json()).notifications || [] : [];
}
